import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Timer } from "lucide-react";
import type { AnswerResponse } from "./types";

interface Props {
  result: AnswerResponse;
  language: string;
  onContinue: () => void;
}

export const RoundFeedback = ({ result, language, onContinue }: Props) => {
  const { t } = useTranslation();
  // Auto-continue after 2.5s
  useEffect(() => {
    const timer = setTimeout(onContinue, 4500);
    return () => clearTimeout(timer);
  }, [onContinue]);

  const explanation =
    language === "es" ? result.explanationEs : result.explanationEn;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onContinue}
    >
      <div
        className={`rounded-2xl p-6 max-w-sm mx-4 shadow-2xl border text-center ${
          result.correct
            ? "bg-green-950/90 border-green-500/30"
            : "bg-red-950/90 border-red-500/30"
        }`}
      >
        {result.timedOut ? (
          <Timer className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
        ) : result.correct ? (
          <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
        ) : (
          <XCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
        )}

        <h3
          className={`text-xl font-bold mb-2 ${
            result.correct ? "text-green-400" : "text-red-400"
          }`}
        >
          {result.timedOut
            ? t('preflop_times_up')
            : result.correct
              ? t('preflop_correct_answer')
              : t('preflop_incorrect')}
        </h3>

        {!result.correct && (
          <div className="mb-3">
            <span className="text-muted-foreground text-sm">{t('preflop_correct_was')} </span>
            <Badge
              variant="outline"
              className={`ml-1 ${
                result.correctAction === "raise"
                  ? "border-secondary text-secondary"
                  : result.correctAction === "fold"
                    ? "border-destructive text-destructive"
                    : "border-blue-400 text-blue-400"
              }`}
            >
              {result.correctAction === "fold" ? t('poker_fold') : result.correctAction === "call" ? t('poker_call') : t('poker_raise')}
            </Badge>
          </div>
        )}

        <p className="text-sm text-muted-foreground leading-relaxed">
          {explanation}
        </p>

        {result.correct && result.streak >= 3 && (
          <div className="mt-3 text-orange-400 font-bold text-sm">
            {t('preflop_streak', { count: result.streak })}
          </div>
        )}

        <p className="text-xs text-muted-foreground/50 mt-4">{t('preflop_tap_continue')}</p>
      </div>
    </div>
  );
};
